import { OfferedProjectType } from "../../../../clients/graphqlTypes.js";
import {
  buildTaxonomyIndex,
  type TaxonomyNode,
} from "../offeredServices/catalogueModel.js";
import { OFFERED_PROJECT_TYPES } from "../offeredServices/projectTypes.js";

export type MatrixSelection = {
  categoryId: string;
  projectType: OfferedProjectType;
};

export type OnboardingFlowWizardState = {
  selectedVerticals: string[];
  selectedProjectTypes: OfferedProjectType[];
  applyToAll: "yes" | "no";
  specifyMode: "unset" | "now" | "later";
  // Matrix-stage row toggle. A category appears here iff the user has
  // declared they offer it; this is independent of `matrixSelections`
  // (declared rows may have zero project-type cells set).
  matrixDeclaredCategories: string[];
  matrixSelections: MatrixSelection[];
};

export const initialOnboardingFlowWizardState: OnboardingFlowWizardState = {
  selectedVerticals: [],
  selectedProjectTypes: [],
  applyToAll: "yes",
  specifyMode: "unset",
  matrixDeclaredCategories: [],
  matrixSelections: [],
};

// Structural shape of the generated `SuggestedOfferedServices` type.
export type SuggestedOfferedServicesInput = {
  categories: Array<string>;
  services: Array<MatrixSelection>;
};

const DELETED_ROOT_ID = "__deleted__";

const withoutDeletedSubtree = (
  categories: readonly TaxonomyNode[]
): TaxonomyNode[] => {
  const index = buildTaxonomyIndex(categories);
  return categories.filter(
    c =>
      c.id !== DELETED_ROOT_ID &&
      !index.ancestorsOf(c.id).includes(DELETED_ROOT_ID)
  );
};

// Wizard start state derived from an organisation's suggested offered services
// (design D13). `null`/`undefined` suggestions give the empty state.
export const initialWizardStateFromSuggestions = (
  suggestions: SuggestedOfferedServicesInput | null | undefined,
  categories: readonly TaxonomyNode[]
): OnboardingFlowWizardState => {
  if (!suggestions) return initialOnboardingFlowWizardState;

  // The matrix and project-types stages drop the "__deleted__" sentinel
  // subtree, so suggestions are mapped against the same usable taxonomy.
  const usable = withoutDeletedSubtree(categories);
  const index = buildTaxonomyIndex(usable);
  const suggested = [
    ...new Set(
      [
        ...suggestions.categories,
        ...suggestions.services
          .map(s => s.categoryId)
          .filter(id => index.isLeaf(id)),
      ].filter(id => index.has(id))
    ),
  ];
  // Services are only shown (and committed) on leaves: the matrix renders
  // action cells for leaf rows only.
  const services = suggestions.services.filter(s => index.isLeaf(s.categoryId));

  const needed = new Set(suggested);
  for (const id of suggested) {
    for (const ancestorId of index.ancestorsOf(id)) needed.add(ancestorId);
  }
  const selectedVerticals = [
    ...new Set(
      usable
        .filter(c => (c.parentId ?? null) === null && needed.has(c.id))
        .map(c => c.id)
    ),
  ];

  const serviceProjectTypes = new Set(services.map(s => s.projectType));
  const selectedProjectTypes = OFFERED_PROJECT_TYPES.filter(pt =>
    serviceProjectTypes.has(pt)
  );

  const hasServices = services.length > 0;
  return {
    selectedVerticals,
    selectedProjectTypes,
    applyToAll: hasServices ? "no" : "yes",
    specifyMode: hasServices ? "now" : "unset",
    matrixDeclaredCategories: hasServices ? suggested : [],
    matrixSelections: hasServices ? services : [],
  };
};

// Discriminated union of reducer actions. Names are imperative-mood; one case
// per action in onboardingFlowWizardReducer.
export type OnboardingFlowWizardAction =
  | { type: "toggleVertical"; categoryId: string }
  | { type: "toggleProjectType"; projectType: OfferedProjectType }
  | { type: "setApplyToAll"; value: "yes" | "no" }
  | { type: "setSpecifyMode"; value: "now" | "later" }
  // Wholesale replace of both declared rows and selections. The cascade
  // CatalogueGrid computes the next buffer (activation cascade, auto-fill,
  // leaf-only actions, session memory) and emits it as one unit; `selections`
  // may retain cells for undeclared categories (session memory) — the commit
  // selector filters those out.
  | {
      type: "setMatrixBuffer";
      declaredCategories: string[];
      selections: MatrixSelection[];
    }
  | { type: "reset" };

// Discriminant for which path the user took through the wizard to arrive at
// the setOrganisationOfferedServices mutation — drives the path → payload
// mapping in selectOfferedServicesPayload.
export type OfferedServicesCommitPath =
  | "skip-project-types"
  | "apply-to-all-yes"
  | "specify-later"
  | "specify-now-commit"
  | "skip-matrix";

export type OfferedServicesPayload = {
  categories: string[];
  services: { categoryId: string; projectType: OfferedProjectType }[];
};
